<template>
  <div>
    <div
      class="tree-node flex items-center gap-1.5 pr-2 cursor-pointer select-none group rounded-md mx-1 transition-all duration-150"
      :class="[
        selectedId === node.id ? 'bg-primary-50/80 text-primary-700' : 'hover:bg-slate-100/60 text-slate-600',
        node.pathOnly || disabled ? 'opacity-50' : ''
      ]"
      :style="{ paddingLeft: paddingLeftPx }"
      @click="handleClick"
    >
      <span
        class="expand-arrow w-4 h-4 flex items-center justify-center shrink-0"
        :class="{ 'invisible': !hasChildren }"
        @click.stop="expanded = !expanded"
      >
        <el-icon :size="12"><ArrowRight v-if="!expanded" /><ArrowDown v-else /></el-icon>
      </span>
      <el-icon :size="16" class="shrink-0" :class="iconClass">
        <FolderOpened v-if="expanded && hasChildren" />
        <Folder v-else />
      </el-icon>
      <span class="flex-1 truncate text-[13px] font-medium" :title="node.name">{{ node.name }}</span>
      <span v-if="disabled" class="text-[10px] px-1.5 py-0.5 rounded shrink-0 bg-red-50 text-red-600 font-medium">
        禁用
      </span>
      <el-dropdown
        v-if="canManage"
        trigger="click"
        :hide-on-click="true"
        class="shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
      >
        <span class="w-6 h-6 flex items-center justify-center rounded hover:bg-slate-200/60 transition-colors" @click.stop>
          <el-icon :size="14" class="text-slate-400"><MoreFilled /></el-icon>
        </span>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item @click="$emit('edit', node)">
              <el-icon :size="14"><Edit /></el-icon>
              编辑
            </el-dropdown-item>
            <el-dropdown-item @click="$emit('move', node)">
              <el-icon :size="14"><Right /></el-icon>
              移动
            </el-dropdown-item>
            <el-dropdown-item @click="$emit('acl', node)">
              <el-icon :size="14"><Lock /></el-icon>
              权限配置
            </el-dropdown-item>
            <el-dropdown-item divided @click="handleDelete">
              <el-icon :size="14"><Delete /></el-icon>
              <span class="text-red-500">删除</span>
            </el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
    </div>
    <div v-if="expanded && hasChildren">
      <FolderTreeNode
        v-for="child in node.children"
        :key="child.id"
        :node="child"
        :selected-id="selectedId"
        :depth="depth + 1"
        @select="$emit('select', $event)"
        @edit="$emit('edit', $event)"
        @delete="$emit('delete', $event)"
        @move="$emit('move', $event)"
        @acl="$emit('acl', $event)"
        @refresh="$emit('refresh')"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import {
  ArrowRight, ArrowDown, Folder, FolderOpened,
  MoreFilled, Edit, Right, Lock, Delete
} from '@element-plus/icons-vue'

const props = defineProps({
  node: { type: Object, required: true },
  selectedId: { type: [Number, String], default: null },
  depth: { type: Number, default: 0 }
})

const emit = defineEmits(['select', 'edit', 'delete', 'move', 'acl', 'refresh'])

const expanded = ref(props.depth < 2)

const hasChildren = computed(() => props.node.children?.length > 0)
const disabled = computed(() => props.node.status === 0 || props.node.status === 'DISABLED')
const canManage = computed(() => !props.node.pathOnly && !disabled.value && props.node.permissionLevel === 'MANAGE')

const paddingLeftPx = computed(() => `${8 + props.depth * 16}px`)

const iconClass = computed(() => {
  if (props.node.pathOnly) return 'text-slate-300'
  if (props.node.permissionLevel === 'MANAGE') return 'text-amber-500'
  return 'text-slate-400'
})

function handleClick() {
  if (!props.node.pathOnly && !disabled.value) {
    emit('select', props.node)
  }
}

function handleDelete() {
  emit('delete', props.node)
}
</script>
